/* eslint-disable ember/no-computed-properties-in-native-classes */
import Controller from '@ember/controller';
import { action, computed, set } from '@ember/object';
import { service } from '@ember/service';
import dayjs from 'dayjs';
import fnMedian from 'frontend-utils/utils/numeric/find-median';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import { formatUrl } from 'lumen/utils/rates-request';

export default class HotelRankingController extends Controller {
  queryParams = [
    { otaId: { scope: 'controller' } },
    { timespan: { scope: 'controller' } },
    { compsetId: { scope: 'controller' } },
  ];

  @service ajax;
  @service dates;
  @service('hotel-and-market') hotelAndMarketService;
  @service intl;
  @service simpleExport;

  compsetId = null;
  loadingStateRows = [
    { empty: false },
    { empty: false },
    { empty: true },
    { empty: true },
    { empty: true },
    { empty: true },
    { empty: true },
    { empty: true },
    { empty: true },
    { empty: true },
  ];

  // Should be local time
  month = null;
  otaId = 'bookingdotcom';
  timespan = 'monthly';

  @computed('currentRankingSummary.ranking')
  get arrivalRankingInfo() {
    return { averageRanking: this.currentRankingSummary?.ranking };
  }

  get currentHotelData() {
    if (this.currentSelectedPeriod) {
      return this.currentSelectedPeriod.find(
        (info) =>
          info.hotelinfo.id === this.hotelAndMarketService.hotel?.hotelinfo?.id,
      );
    }
    return undefined;
  }

  @computed('hotelAndMarketService.hotel.hotelinfo.id', 'selectedOTA.rankings')
  get currentRankingSummary() {
    return this.selectedOTA.rankings.find(
      (ranking) =>
        ranking.hotelinfo.id ===
        this.hotelAndMarketService.hotel?.hotelinfo?.id,
    );
  }

  @computed('selectedOTA.rankings')
  get currentSelectedPeriod() {
    if (this.selectedOTA) {
      return this.selectedOTA.rankings.sort((a, b) => {
        if (a.ranking <= 0) {
          return 1;
        }
        if (b.ranking <= 0) {
          return -1;
        }

        return a.ranking - b.ranking;
      });
    }

    return null;
  }

  @computed('compsetId', 'model.rankings.[]', 'selectedOTA.id', 'timespan')
  get dataForChart() {
    return this.model?.rankings.filter(
      (rank) => rank.ota?.id === this.selectedOTA?.id,
    );
  }

  @computed('monthMoment', 'model.periods.[]')
  get firstArrivalPeriod() {
    const sortedPeriods = this.model?.periods
      .filter((period) => period.fromDate.isSame(this.monthMoment, 'month'))
      .sort((a, b) => a.day.integerDate - b.day.integerDate);
    return sortedPeriods[0];
  }

  get hasCompsetFilter() {
    return this.hotelAndMarketService.hasSecondaryCompset;
  }

  get hasUpdateTimestamp() {
    return Boolean(this.updateTimestamp);
  }

  get hotelId() {
    return this.hotelAndMarketService.hotel?.id;
  }

  get localisedFormat() {
    return this.dates.partialDateFormat?.YEAR_AND_MONTH_LONG;
  }

  @computed('month')
  get monthMoment() {
    return this.hotelAndMarketService.toLocalTime(`${this.month}-15`);
  }

  @computed(
    // eslint-disable-next-line ember/use-brace-expansion
    'firstArrivalPeriod.fromDate',
    'hotelAndMarketService.hotel.defaultLosRates',
    'model.loading',
    'model.periods.[]',
    'model.periods.loading',
    'month',
    'monthMoment',
  )
  get monthOnlyModel() {
    const res = [];

    if (!this.model?.periods?.loading) {
      const startOfMonth = this.hotelAndMarketService.toLocalTime(
        this.monthMoment.startOf('month').startOf('week').format('YYYY-MM-DD'),
      );
      const endOfMonth = this.hotelAndMarketService.toLocalTime(
        this.monthMoment.endOf('month').endOf('week').format('YYYY-MM-DD'),
      );
      const daysInMonth = endOfMonth.diff(startOfMonth, 'day');

      for (let i = daysInMonth; i >= 0; i--) {
        res[i] = {};
      }

      const firstDate = this.firstArrivalPeriod?.fromDate;
      const today = this.hotelAndMarketService.getLocalTime();
      let startDate;
      let startIndex = 0;
      if (
        this.monthMoment.isSame(today, 'month') ||
        firstDate.isBefore(today, 'day') ||
        startOfMonth.isBefore(today, 'day')
      ) {
        startDate = today;
        startIndex = today.diff(startOfMonth, 'day');
      } else {
        startDate = startOfMonth;
      }
      const endDate = endOfMonth;
      const days = this.model?.periods
        .sort((a, b) => a.day.integerDate - b.day.integerDate)
        .filter(
          (x) =>
            x.los === this.hotelAndMarketService.hotel.defaultLosRates[0] &&
            x.fromDate.isBetween(startDate, endDate, 'day', '[]'),
        );

      for (let k = 0; k < days.length; k++) {
        res[k + startIndex] = days[k];
      }
    }

    // We need the loading state of the original model for the calendar loading state to work
    res.loading = this.model?.loading;

    return res;
  }

  @computed('model.periods', 'monthOnlyModel.length')
  get nextMonthDisabled() {
    const modelPeriods = this.model?.periods ?? [];
    const lastOfMonthOnlyModel =
      this.monthOnlyModel?.[this.monthOnlyModel.length - 1];
    if (
      (lastOfMonthOnlyModel &&
        Object.keys(lastOfMonthOnlyModel).length === 0) ||
      lastOfMonthOnlyModel === modelPeriods[modelPeriods.length - 1]
    ) {
      return true;
    }
    return false;
  }

  @computed(
    // eslint-disable-next-line ember/use-brace-expansion
    'hotelAndMarketService.hotel.OTAsRanking',
    'hotelAndMarketService.hotel.hotelinfo.id',
    'model.loading',
    'model.rankings.[]',
    'otaId',
    'sortedModel',
    'timespan',
  )
  get otas() {
    const res = {};
    // Will need to be expanded when more than two options are available
    // let sortedModels = get(this, 'sortedModels');
    const rankings = this.sortedModel;
    const rankingOtas = this.hotelAndMarketService.hotel?.OTAsRanking;
    // Structure of res object is as follows:
    // 1st level is ota (indexed by id) wich contains the ota object and ranking object
    // ranking object contains ranking and additional info per hotelinfo
    rankingOtas.forEach((ota) => {
      res[ota.id] = {
        id: ota.id,
        ota,
        rankings: [],
        selected: ota.id === this.otaId,
      };

      if (!this.model?.loading) {
        const otaRankings = rankings[ota.id];

        Object.keys(otaRankings).forEach((hotel) => {
          const ranking = otaRankings[hotel][0];
          const previousRanking = otaRankings[hotel][1];

          // Determine difference with previous period
          if (ranking) {
            set(
              ranking,
              'reviewScore',
              Math.round(ranking.reviewScore * 100) / 100,
            );
            if (previousRanking) {
              if (previousRanking.ranking > 0 && ranking.ranking > 0) {
                set(
                  ranking,
                  'ranking_diff',
                  previousRanking.ranking - ranking.ranking,
                );

                // + sign if positive
                let rankingDiff;
                if (ranking.ranking_diff > 0) {
                  rankingDiff = `+${ranking.ranking_diff}`;
                }
                if (ranking.ranking_diff < 0) {
                  rankingDiff = ranking.ranking_diff;
                }
                set(ranking, 'ranking_diff', rankingDiff);

                // Coloring green if positive and red if negative
                let rankingClass = '';
                if (Number(ranking.ranking_diff) > 0) {
                  rankingClass = 'green-ok';
                }
                if (Number(ranking.ranking_diff) < 0) {
                  rankingClass = 'red-nok';
                }
                set(ranking, 'ranking_class', rankingClass);
              }
              if (previousRanking.reviewScore > 0 && ranking.reviewScore > 0) {
                set(
                  ranking,
                  'review_diff',
                  Math.round(
                    (ranking.reviewScore -
                      Math.round(previousRanking.reviewScore * 10) / 10) *
                      10,
                  ) / 10,
                );

                // + sign if positive
                let reviewDiff;
                if (ranking.review_diff > 0) {
                  reviewDiff = `+${ranking.review_diff}`;
                }
                if (ranking.review_diff < 0) {
                  reviewDiff = ranking.review_diff;
                }
                set(ranking, 'review_diff', reviewDiff);

                // Coloring green if positive and red if negative
                let rankingClass = '';
                if (Number(ranking.review_diff) > 0) {
                  rankingClass = 'green-ok';
                }
                if (Number(ranking.review_diff) < 0) {
                  rankingClass = 'red-nok';
                }
                set(ranking, 'review_class', rankingClass);
              }
            }

            if (hotel === this.hotelAndMarketService.hotel?.hotelinfo?.id) {
              set(ranking, 'selected', true);
            }
            res[ota.id].rankings.push(ranking);
          }
        });
      }
    });
    return res;
  }

  @computed('firstArrivalPeriod.fromDate', 'monthMoment')
  get previousMonthDisabled() {
    if (
      this.firstArrivalPeriod?.fromDate &&
      this.firstArrivalPeriod?.fromDate.isBefore(
        this.hotelAndMarketService.getLocalTime(),
        'day',
      )
    ) {
      return true;
    }
    return Boolean(
      this.monthMoment.isSame(
        this.hotelAndMarketService.getLocalTime(),
        'month',
      ),
    );
  }

  /**
   * Determines the max amaount of properties listed in a given destination
   * This will give the correct amount even if there is missing data
   * Note: missing data will have availableProperties set to zero
   * We start at the most recent date and trace back from there
   * We can stop if we find the most recent amount of available hotels for a destination or if we have checked every rankingsummary
   * @return { int }   : the max number of properties listed in a destination
   */
  @computed('dataForChart.[]')
  get propertiesListed() {
    let propertiesListed = 0;
    const dataForChart = this.dataForChart
      .slice()
      .sort((a, b) => a.integerDate - b.integerDate);
    let index = dataForChart.length - 1;

    while (propertiesListed === 0 && index >= 0) {
      const ranking = dataForChart[index];
      if (ranking.availableHotels > propertiesListed) {
        propertiesListed = ranking.availableHotels;
      }
      index -= 1;
    }
    return propertiesListed;
  }

  get rankingOtaOptions() {
    return this.hotelAndMarketService.hotel.OTAsRanking.map((ota) => ({
      id: ota.id,
      label: ota.name,
      iconBrand: ota.id,
    }));
  }

  @computed('timespan', 'timespans')
  get selectedDuration() {
    if (this.timespan === 'monthly') {
      return this.timespans[0];
    }
    if (this.timespan === 'weekly') {
      return this.timespans[1];
    }

    return null;
  }

  @computed('otas', 'otaId')
  get selectedOTA() {
    if (this.otas && this.otas[this.otaId]) {
      return this.otas[this.otaId];
    }

    return null;
  }

  @computed('otaId', 'rankingOtaOptions')
  get selectedRankingOta() {
    return this.rankingOtaOptions.find((ota) => ota.id === this.otaId);
  }

  get showUpdateTime() {
    return !this.monthMoment

      .endOf('month')
      .isBefore(this.hotelAndMarketService.getLocalTime());
  }

  @computed(
    'hotelAndMarketService.hotel.OTAsRanking',
    'model.rankings.[]',
    'timespan',
  )
  get sortedModel() {
    const res = {};
    const rankingOtas = this.hotelAndMarketService.hotel?.OTAsRanking;
    rankingOtas.forEach((ota) => {
      res[ota.id] = {};
    });
    this.model?.rankings.forEach((ranking) => {
      if (res[ranking.ota?.id]) {
        if (!res[ranking.ota?.id][ranking.hotelinfo?.id]) {
          res[ranking.ota.id][ranking.hotelinfo.id] = [];
        }
        res[ranking.ota?.id][ranking.hotelinfo?.id].push(ranking);
      }
    });

    Object.keys(res).forEach((ota) => {
      Object.keys(res[ota]).forEach((hotel) => {
        res[ota][hotel].sort(
          (a, b) => b.extractDatetime.unix() - a.extractDatetime.unix(),
        );
      });
    });

    return res;
  }

  /**
   * Needs to be update with user locale
   * @return { array }   : options of the timespan of the ranking
   */
  @computed('hotelAndMarketService.user.locale')
  get timespans() {
    return [
      { id: 'monthly', label: this.intl.t('ranking.monthly_change') },
      { id: 'weekly', label: this.intl.t('ranking.weekly_change') },
    ];
  }

  @computed('model.periods.[]')
  get updateTimestamp() {
    const minPerDay = [];
    let updateTimestamp = null;
    const model = this.model?.periods;
    model.forEach((period) => {
      const updateTimesUnix = [];
      const { rankings } = period;
      Object.keys(rankings ?? {}).forEach((hotel) => {
        const extractDateTime = dayjs
          .utc(rankings[hotel][0].extract_datetime)
          .unix();
        if (extractDateTime > 0) {
          updateTimesUnix.push(extractDateTime);
        }
      });
      if (updateTimesUnix.length > 0) {
        minPerDay.push(fnMedian(updateTimesUnix));
      }
    });

    if (minPerDay.length) {
      const median = Math.max.apply(null, minPerDay);
      updateTimestamp = dayjs.unix(median).fromNow();
    }
    return updateTimestamp;
  }

  @action
  doRefresh() {
    // Bubble to route
    return true;
  }

  // Ranking/Review score export
  @action
  exportRanking(ranking) {
    if (!this.model?.loading) {
      const hotels = [this.hotelAndMarketService.hotel?.hotelinfo];
      const selectedOTA = this.otaId;
      const data = this.sortedModel[this.otaId];
      this.hotelAndMarketService.activeCompset?.competitors.forEach((item) => {
        // Add competitors based on selectedOTA instead of OTA
        if (
          item.otas.map((ota) => ota.id).includes(selectedOTA) &&
          data[item.id]
        ) {
          hotels.push(item);
        }
      });

      let headers = ['Period'];
      headers = [...headers, ...hotels.map((hotel) => hotel.name)];

      const title = this.modalTitle;
      let sheetName = ranking
        ? this.intl.t('menu.ranking').toString()
        : this.intl.t('ranking.review_score').toString();

      if (this.selectedRankingOta?.label) {
        sheetName += ` ${this.selectedRankingOta?.label}`;
      }

      const weekly = this.timespan === 'weekly';
      const dateStampFormat = weekly ? 'LL ([W]w)' : this.localisedFormat;

      const nrOfDates =
        data[this.hotelAndMarketService.hotel?.hotelinfo?.id].length;
      const content = [];
      const formatHotel = function (hotel) {
        const currentData = data[hotel.id][this.i];
        if (currentData) {
          if (ranking) {
            this.row.push(currentData.ranking);
          } else {
            const score = currentData.reviewScore;
            if (score) {
              this.row.push(Math.round(score * 10) / 10);
            } else {
              this.row.push('');
            }
          }
        } else {
          this.row.push('');
        }
      };

      for (let i = 0; i < nrOfDates; i++) {
        const row = [];
        row.push(
          data[this.hotelAndMarketService.hotel?.hotelinfo?.id][
            i
          ].extractDatetime.format(dateStampFormat),
        );
        const ctx = { row, i };
        hotels.forEach(formatHotel, ctx);
        content.push(row);
      }

      const postData = JSON.stringify({
        headers,
        content,
        title,
        sheetName,
      });

      this.simpleExport.execute(postData, this.hotelId);
    }
  }

  // Ranking per arrival date
  @action
  handleExport() {
    const url = formatUrl(
      '/apigateway/v1/app/download/ranking',
      {
        hotel_id: this.hotelAndMarketService.hotel?.id,
        ota: this.otaId,
        compset_ids: this.compsetId,
        report_type: 'ranking',
        los: this.hotelAndMarketService.hotel.defaultLosRates[0],
      },
      this.ajax.host,
    );

    openUrl(url);
  }

  @action
  initMonth() {
    if (!this.month) {
      set(
        this,
        'month',
        this.hotelAndMarketService.getLocalTime().format('YYYY-MM'),
      );
    }
  }

  @action
  nextMonth() {
    set(this, 'month', this.monthMoment.add(1, 'month').format('YYYY-MM'));
  }

  @action
  previousMonth() {
    set(this, 'month', this.monthMoment.subtract(1, 'month').format('YYYY-MM'));
  }

  @action
  setCompsetId(compsetId) {
    set(this, 'compsetId', compsetId);
  }

  @action
  setSelected(ota) {
    const { otas } = this;
    set(otas[this.otaId], 'selected', false);
    set(otas[ota.id], 'selected', true);
    set(this, 'otaId', ota.id);
    set(
      this,
      'month',
      this.hotelAndMarketService.getLocalTime().format('YYYY-MM'),
    );
  }

  @action
  setTimespan(timespan) {
    set(this, 'timespan', timespan.id);
  }
}
